import React from 'react'
import { Box, Typography } from '@mui/material'
import { dsColorNew } from '@palette'

/**
 * A numbered block inside a form, with a line saying what it configures.
 *
 * Lifted out of the Schedule rules drawer (2026-09-24) because the
 * Strategies wizard needed the same thing and had a weaker version of
 * it: a 13px title and an optional caption, no number, no icon. The
 * number is what turns a column of inputs into a short list of
 * decisions you can count, and the subtitle is what stops an operator
 * guessing what an axis means — which is exactly how the Sites filter
 * went unnoticed as broken for days.
 *
 * One component, both screens, so they cannot drift apart again.
 */
// ─── Section card with step number, icon, title + subtitle ─────────────
// In the look of the app's form sections (`AppFormSection`): white card, the number in the same
// soft tile the sections give their icon, and the body under a hairline.
export default function SectionCard({ step, title, subtitle, icon, children }) {
	return (
		// `minWidth: 0` so a card holding a scrollable table scrolls inside
		// itself instead of widening everything above it.
		<Box component="section" sx={{ bgcolor: dsColorNew.white, borderRadius: '14px', minWidth: 0,
		                               border: `1px solid ${dsColorNew.grayBorder}` }}>
			<Box display="flex" alignItems="flex-start" gap={1.25}
			     sx={{ px: 2.25, py: 1.75, borderBottom: `1px solid ${dsColorNew.grayBorder}` }}>
				<Box sx={{
					width: 28, height: 28, borderRadius: '8px',
					bgcolor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue,
					display: 'flex', alignItems: 'center',
					justifyContent: 'center', fontSize: 12,
					fontWeight: 600, flexShrink: 0,
				}}>
					{step}
				</Box>
				<Box flex={1} minWidth={0} sx={{ pt: '4px' }}>
					<Box display="flex" alignItems="center" gap={0.75}>
						{icon && (
							<Box sx={{ color: dsColorNew.textMuted, display: 'flex' }}>
								{React.cloneElement(icon, { sx: { fontSize: 16 }})}
							</Box>
						)}
						<Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.45,
						                                  color: dsColorNew.textTitle }}>
							{title}
						</Typography>
					</Box>
					{subtitle && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
						                  mt: 0.25, lineHeight: 1.45 }}>
							{subtitle}
						</Typography>
					)}
				</Box>
			</Box>
			<Box sx={{
				p: 2.25,
				// labels that hide the field border behind them (date pickers) take the card's white
				'& .MuiInputLabel-root.MuiInputLabel-shrink': { backgroundColor: dsColorNew.white },
			}}>
				{children}
			</Box>
		</Box>
	)
}
